import { createContext, useState, useEffect, useContext } from "react";
import { supabase } from "../supabaseClient";

export const TimezoneContext = createContext();

const DEFAULT_TIMEZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export const TimezoneProvider = ({ children, userId }) => {
  const [userTimezone, setUserTimezone] = useState(DEFAULT_TIMEZONE);
  const [loading, setLoading] = useState(true);

  // Fetch timezone from user profile
  useEffect(() => {
    const fetchTimezone = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("time_zone")
          .eq("id", userId)
          .single();

        if (error) {
          console.error("Error fetching user timezone:", error);
          setUserTimezone(DEFAULT_TIMEZONE);
        } else {
          setUserTimezone(profile?.time_zone || DEFAULT_TIMEZONE);
        }
      } catch (err) {
        console.error("Error fetching user timezone:", err);
        setUserTimezone(DEFAULT_TIMEZONE);
      } finally {
        setLoading(false);
      }
    };

    fetchTimezone();
  }, [userId]);

  // Function to update timezone (called when user updates profile)
  const updateTimezone = (newTimezone) => {
    setUserTimezone(newTimezone || DEFAULT_TIMEZONE);
  };

  return (
    <TimezoneContext.Provider value={{ userTimezone, updateTimezone, loading }}>
      {children}
    </TimezoneContext.Provider>
  );
};

// Custom hook for easy access to timezone
export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    console.warn("useTimezone must be used within a TimezoneProvider");
    return { userTimezone: DEFAULT_TIMEZONE, updateTimezone: () => {}, loading: false };
  }
  return context;
};
